import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { downloadReport, getReports } from '../services/api';

const ReportPage = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [reportReady, setReportReady] = useState(false);

  useEffect(() => {
    const checkReport = async () => {
      try {
        const response = await getReports(contactId);
        const reports = response.data;
        if (reports && reports.length > 0) {
          setReportReady(true);
        } else {
          setError('Report is not ready yet. Please wait a moment and refresh.');
        }
      } catch (err) {
        setError('Unable to check report status. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    checkReport();
  }, [contactId]);

  const handleDownload = async () => {
    setDownloading(true);
    setError('');

    try {
      const response = await downloadReport(contactId);
      const { url } = response.data;

      // Open download URL in new tab
      window.open(url, '_blank');
    } catch (err) {
      setError('Unable to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleStartNew = () => {
    navigate('/start');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
        >
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: 'white' }} />
            </Box>

            <Typography
              variant="h3"
              sx={{
                mb: 2,
                color: 'primary.main',
                fontWeight: 700,
              }}
            >
              Your Report is Ready
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 4,
                color: 'text.secondary',
                fontSize: '1.125rem',
              }}
            >
              Thank you for completing the Operational Health Diagnostic.
              Your personalized assessment report is ready for download.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {error}
              </Alert>
            )}

            {reportReady && (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleDownload}
                disabled={downloading}
                startIcon={
                  downloading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                sx={{
                  minWidth: 220,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                {downloading ? 'Preparing...' : 'Download Report'}
              </Button>
            )}

            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                }}
              >
                A member of our team will follow up with you within
                24-48 hours to discuss your results and answer any questions.
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Questions? Contact us at{' '}
                <Typography
                  component="a"
                  href="mailto:support@example.com"
                  sx={{
                    color: 'secondary.main',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  support@example.com
                </Typography>
              </Typography>
            </Box>

            <Button
              variant="text"
              onClick={handleStartNew}
              sx={{
                mt: 4,
                color: 'primary.main',
              }}
            >
              Start a New Assessment
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ReportPage;
